import { retrieveProductList } from '@/api/product'
import { buriedPointUtil, gaAnalytics, metaPixelTrack } from '@/utils/utils'
import { createSharedComposable, useEventListener } from '@vueuse/core'
import { useListProcess } from './useListProcess'
import { useDevice } from '@/hooks/useDevice'
import { useTextSearch } from '@/hooks/useTextSearch'
import { useLanguageStore } from '@/pinia/language'

/**
 * @returns {object}
 * filter - 返回过滤项配置
 * loading - 当前是否处于请求列表数据中
 * list - 返回列表数据
 * onLoad - 加载更多
 * onReset - 重置 page / loading / finished 状态并触发 onLoad 事件
 */
export const useProductList = createSharedComposable(() => {
  const route = useRoute()
  const { list, handleRawList } = useListProcess()
  const { deviceType } = useDevice()
  const { insertTextSerachRecord } = useTextSearch()
  const language = useLanguageStore()

  const loading = ref(false)
  const filter = reactive({
    keyword: route.query.keyword,
    categoryId: route.query.categoryId,
    language: language.current,
    sortMap: null
  })
  /**
   * 存储已翻译后的 keyword，来减少 keyword 翻译次数
   */
  const translatedKeyword = reactive({
    source: filter.keyword,
    /**
     * Ai Search 会提前获取到翻译后的 keyword，这样就不要再翻译关键词了
     */
    target: route.query.keywordCn ?? (filter.language === 'zh' ? filter.keyword : null)
  })
  const page = reactive({
    pageNow: 1,
    pageSize: 50
  })

  useEventListener(window, 'scroll', (e) => {
    if (loading.value) return
    const el = e.target.scrollingElement
    if (el.scrollHeight - el.clientHeight - el.scrollTop < 75) {
      onLoad()
    }
  })

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const onLoad = async () => {
    if (loading.value) return
    const currentRequestId = ++latestRequestId
    const data = { ...filter, ...page }
    loading.value = true
    if (data.keyword !== translatedKeyword.source || !translatedKeyword.target) {
      const { keyWordCn } = await translateKeyword(data)
      translatedKeyword.source = filter.keyword
      translatedKeyword.target = keyWordCn
    }
    data.keyword = translatedKeyword.target
    data.language = 'zh'
    const rows = await getProductList(data)
    if (currentRequestId === latestRequestId) {
      list.value = [...list.value, ...handleRawList(rows)]
      loading.value = false
      page.pageNow++
    }
  }

  const translateKeyword = async (filter) => {
    const { code, data, msg } = await retrieveProductList({
      ...filter,
      async: true
    })
    if (code === 200) {
      return {
        keyWordCn: data.keyWordCn
      }
    } else {
      throw new Error(msg)
    }
  }

  const getProductList = async (params) => {
    const { code, data, msg } = await retrieveProductList({
      ...params,
      async: false
    })
    if (deviceType.value === 'MOBILE') {
      buriedPointUtil({ eventId: 'MPSC201' })
      gaAnalytics('mp_search_page_view', 'mp_search_page_view')
      if (code === 401) {
        buriedPointUtil({ eventId: 'MPLG501' })
      }
      if (params.pageNow > 1) {
        buriedPointUtil({ eventId: 'MPSC401', others: { ...params } })
      }
    } else {
      if (params.pageNow > 1) {
        buriedPointUtil({ eventId: 'PSC401', others: { ...params } })
      }
    }
    if (code !== 200) {
      throw new Error(msg)
    }
    /**
     * Meta Pixel 埋点：全局搜索(含类目搜索)触发
     */
    if (params.pageNow === 1 && params.keyword != null && params.keyword !== '') {
      metaPixelTrack('PSC401', 'Search', {
        content_ids: data.rows.slice(0, 10).map((item) => item.offerId),
        content_type: 'product_group',
        currency: '$',
        value: 0.1,
        search_string: filter.keyword
      })
    }
    return data.rows
  }

  onBeforeMount(() => {
    onLoad()
  })

  watch(() => filter.keyword, () => {
    if (filter.keyword) {
      insertTextSerachRecord(filter.keyword)
    }
  }, {
    immediate: true
  })

  const onReset = () => {
    page.pageNow = 1
    list.value = []
    loading.value = false
    onLoad()
  }

  return { filter, loading, list, onLoad, onReset }
})
